import { AxiosError } from 'axios'
import { intervalToDuration } from 'date-fns/intervalToDuration'
import { flatten } from 'flat'
import { FormikContextType, FormikErrors, FormikHelpers, setIn } from 'formik'
import { StatusCodes } from 'http-status-codes'
import numWords from 'num-words'
import queryString from 'query-string'
import React from 'react'
import { onlyText } from 'react-children-utilities'
import invariant from 'tiny-invariant'
import { objectKeys } from 'ts-extras'
import isNumericValidator from 'validator/es/lib/isNumeric'

import embedCodeTemplate from '@/assets/html-templates/embed-code.html?raw'
import { SelectRegulatorOption } from '@/components/form/SelectRegulatorInput'
import { DATE_FORMATS, ORDER_DIRECTION } from '@/lib/constants'
import { parseDate } from '@/lib/helpers/dateHelpers'
import { isDevelopment } from '@/lib/helpers/envHelpers'
import { scrollToFirstError } from '@/lib/helpers/formHelpers'
import { matchesPaths } from '@/lib/helpers/routeHelpers'
import { PDF_ROUTES, ROUTES } from '@/lib/routes'
import constraintViolationService from '@/lib/services/constraintViolationService'
import {
  ConstraintViolationListError,
  HydraCollection,
  OrderDirection,
} from '@/types/api'
import { LocationEntity } from '@/types/entities'
import {
  AddressParts,
  FormErrors,
  SerializableFile,
  StringMap,
} from '@/types/misc'
import { RegulatorItem } from '@/types/responses/common-data'

export function extractURL(url?: string | null): URL | null {
  if (url) {
    if (!url.match(/^[a-zA-Z]+:\/\//)) {
      url = 'http://' + url
    }
    const safeUrl = toSafeHttpUrl(url)
    return safeUrl ? new URL(safeUrl) : null
  }
  return null
}

/**
 * Company-entered links (booking links, websites, privacy policies) are shown
 * to users at other companies. Only http(s) links are allowed through so a
 * `javascript:` link can't run code in the viewer's session.
 *
 * Pass a `baseUrl` to also allow relative links, resolved against it.
 */
export function toSafeHttpUrl(
  url?: string | null,
  baseUrl?: string,
): string | null {
  if (!url) {
    return null
  }

  try {
    const parsedUrl = new URL(url, baseUrl)
    return ['http:', 'https:'].includes(parsedUrl.protocol)
      ? parsedUrl.href
      : null
  } catch {
    return null
  }
}

export function startsWithVowel(word: string): boolean {
  return /^[aeiou]/i.test(word)
}

export function pluralize(
  count: number,
  noun: string,
  suffix = 's',
  displayCount = true,
): string {
  return `${displayCount ? count : ''} ${noun}${count !== 1 ? suffix : ''}`
}

export function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/\s/g, '-')
    .replace(/[^\w-]/g, '')
}

export function capitalizeFirstLetter(string: string): string {
  if (!string) {
    return ''
  }

  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function bool2Human(bool: boolean | undefined): string {
  return bool ? 'Yes' : 'No'
}

export function isConstraintViolationListError(
  error: unknown,
): error is ConstraintViolationListError {
  return (
    error instanceof AxiosError &&
    error.response?.status === StatusCodes.UNPROCESSABLE_ENTITY &&
    error.response.data['@type'] === 'ConstraintViolation' &&
    Array.isArray(error.response.data['violations'])
  )
}

// TODO: Move to errorTypeService.ts
export function isUnauthorizedError(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    error.response?.status === StatusCodes.UNAUTHORIZED
  )
}

export function getErrorDebugInfo(error: unknown) {
  let debugInfo: { [key: string]: unknown } = {}
  if (error instanceof AxiosError) {
    const responseData = error.response?.data
    responseData['trace'] = undefined
    debugInfo = {
      name: error.name,
      message: error.message,
      responseData: responseData,
    }
  } else if (error instanceof Error) {
    debugInfo = {
      name: error.name,
      message: error.message,
    }
  } else {
    debugInfo = JSON.parse(
      JSON.stringify(error, Object.getOwnPropertyNames(error)),
    )
  }

  return debugInfo
}

// Get text content of React element
// Inspired by https://stackoverflow.com/a/60564620/2302835
export function getNodeText(
  node: React.ReactElement | React.ReactNode | string | number,
): string {
  return onlyText(node)
}

export function getTooltipClassName(
  text: React.ReactElement | React.ReactNode | string,
): string {
  const length = getNodeText(text).length

  switch (true) {
    case length > 900:
      return 'max-w-[700px]'
    case length > 200:
      return 'max-w-[250px]'
    case length > 20:
      return 'max-w-[250px]'
    default:
      return 'max-w-[250px]'
  }
}

export function scrollToTop(options?: ScrollToOptions) {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto', ...options })
}

export function scrollToBottom() {
  const scrollingElement = document.scrollingElement || document.body

  scrollingElement.scrollTop = scrollingElement.scrollHeight
}

export function getCurrentAgeFromDob(dateOfBirth?: string): number | undefined {
  if (!dateOfBirth) {
    return
  }

  try {
    return intervalToDuration({
      start: parseDate(dateOfBirth, DATE_FORMATS.DAY_MONTH_YEAR),
      end: new Date(),
    }).years
  } catch (_err) {
    return
  }
}

// https://stackoverflow.com/a/8831937/2302835
export function hashCode(str: string) {
  let hash = 0

  for (let i = 0, len = str.length; i < len; i++) {
    const chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }

  return hash.toString()
}

export function formatMoney(
  moneyValue: number,
  numberFormatOptions?: Intl.NumberFormatOptions,
): string {
  const formattedAmount = Intl.NumberFormat('en-GB', {
    notation: 'standard',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...numberFormatOptions,
  }).format(moneyValue)

  return `£${formattedAmount}`
}

export function formatNumber(
  num: number,
  numberFormatOptions?: Intl.NumberFormatOptions,
): string {
  return Intl.NumberFormat('en-GB', {
    notation: 'standard',
    maximumFractionDigits: 2,
    ...numberFormatOptions,
  }).format(num)
}

export function formatMoneyCompact(
  moneyValue: number,
  shouldRound?: boolean,
): string {
  const valueToFormat = shouldRound ? Math.round(moneyValue) : moneyValue

  const formattedAmount = Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(valueToFormat)

  return `£${formattedAmount}`
}

/**
 * An address on one line: "12 Acacia Avenue, London, SW6 4TN", or whichever parts we hold.
 *
 * Null when we hold none of them, so a caller can say "Not recorded" rather than show an empty row.
 */
export function formatAddress(address?: AddressParts | null): string | null {
  if (!address) {
    return null
  }

  return (
    [address.addressLine1, address.city, address.postcode]
      .filter(Boolean)
      .join(', ') || null
  )
}

/**
 * The office's address on one line, e.g. "1 High Street, Leeds, LS1 1AA,
 * United Kingdom", leaving out any empty parts.
 */
export function formatLocationAddress(
  location: Pick<
    LocationEntity,
    'addressLine1' | 'addressLine2' | 'townOrCity' | 'postcode' | 'country'
  >,
): string {
  return [
    location.addressLine1,
    location.addressLine2,
    location.townOrCity,
    location.postcode,
    location.country?.name,
  ]
    .filter((addressPart) => !!addressPart)
    .join(', ')
}

// Can be extended to include other URLS in the future.
export function isPdfReport() {
  return matchesPaths(window.location.pathname, PDF_ROUTES)
}

// Not sure about this approach. Maybe there's a cleaner way to check if we're
// in the compass flow (maybe React Context?).
export function isCompassFlow() {
  const flowPaths = [
    ROUTES.userCompass(),
    ROUTES.companyCompass(),
    ROUTES.compassStart(),
    ROUTES.compassUpdate(),
  ]

  return flowPaths.includes(window.location.pathname)
}

export function arrayToCommaSeparatedString(
  arr: Array<string | number>,
): string {
  if (arr.length === 0) {
    return ''
  }

  if (arr.length === 1 && arr[0]) {
    return arr[0].toString()
  }

  if (arr.length === 2) {
    return `${arr[0]} and ${arr[1]}`
  }

  return (
    arr.slice(0, arr.length - 1).join(', ') + ', and ' + arr[arr.length - 1]
  )
}

// TODO: See if there's a well-maintained library that does this
// https://stackoverflow.com/a/52311051/2302835
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      let encoded = (reader.result || '').toString().replace(/^data:(.*,)?/, '')
      if (encoded.length % 4 > 0) {
        encoded += '='.repeat(4 - (encoded.length % 4))
      }
      resolve(encoded)
    }
    reader.onerror = (error) => reject(error)
  })
}

export function getNextPageParamFromResponse(
  response: HydraCollection<unknown>,
): number | undefined {
  const nextPageEndpoint = response['hydra:view']
    ? response['hydra:view']['hydra:next']
    : undefined

  return nextPageEndpoint
    ? Number(queryString.parse(nextPageEndpoint)['page'])
    : undefined
}

export function getPreviousPageParamFromResponse(
  response: HydraCollection<unknown>,
): number | undefined {
  const previousPageEndpoint = response['hydra:view']
    ? response['hydra:view']['hydra:previous']
    : undefined

  return previousPageEndpoint
    ? Number(queryString.parse(previousPageEndpoint)['page'])
    : undefined
}

export function getNormalizedFileType(file: File): string {
  const fileType = file?.type ? file.type.split('/')[1] || '' : 'file'

  if (fileType.includes('ms-powerpoint')) {
    return 'PPT'
  }

  if (/ms-?word/.test(fileType)) {
    return 'DOC'
  }

  if (fileType.includes('ms-excel')) {
    return 'SPREADSHEET'
  }

  return fileType.toUpperCase()
}

export function prettify(str?: string): string {
  if (!str) {
    return ''
  }

  let prettified = str.replaceAll('_', ' ')
  prettified = capitalizeFirstLetter(prettified)
  return prettified
}

export function getIdFromIri(iri: IRI): number {
  const urlSegments = (iri || '').split('/')

  if (urlSegments.length <= 1) {
    throw new Error('Invalid IRI')
  }

  return Number(urlSegments[urlSegments.length - 1])
}

export function getNextStep<Steps extends string[]>(
  steps: Steps,
  currentStep: Steps[number],
): Steps[number] {
  if (!steps.includes(currentStep)) {
    console.warn(
      'Invalid step: %s. Valid steps are %s',
      currentStep,
      steps.join(', '),
    )
  }

  const currentStepIndex = steps.indexOf(currentStep)
  const hasNextStep = currentStepIndex <= steps.length - 2

  const nextStep = hasNextStep
    ? steps[currentStepIndex + 1]
    : steps[currentStepIndex]

  invariant(nextStep, 'Next step not found')

  return nextStep
}

export function getPreviousStep<Steps extends string[]>(
  steps: Steps,
  currentStep: Steps[number],
): Steps[number] {
  if (!steps.includes(currentStep)) {
    console.warn(
      'Invalid step: %s. Valid steps are %s',
      currentStep,
      steps.join(', '),
    )
  }

  const stepIndex = steps.indexOf(currentStep)
  const hasPreviousStep = stepIndex >= 1

  return (hasPreviousStep ? steps[stepIndex - 1] : steps[stepIndex]) || ''
}

export function setFieldErrors<Values = unknown>(
  formattedErrors: FormErrors,
  formikHelpers: FormikHelpers<Values>,
) {
  Object.entries(formattedErrors).forEach(([fieldName, errorMessage]) => {
    formikHelpers.setFieldError(fieldName, errorMessage)

    // We must explicitly mark fields as touched if they weren't
    // given any initial value or the initial value was undefined.
    // https://github.com/jaredpalmer/formik/issues/445#issuecomment-551133369
    formikHelpers.setFieldTouched(fieldName, true, false)
  })
}

// Use this helper when you need to explicitly mark some fields as touched so
// that Yup-based validation works for fields without an initial value or with
// an initial value of undefined.
// https://github.com/jaredpalmer/formik/issues/445#issuecomment-551133369
export function markFieldsAsTouched(
  fieldNames: string[],
  setFieldTouched: FormikContextType<unknown>['setFieldTouched'],
) {
  fieldNames.forEach((fieldName) => {
    setFieldTouched(fieldName, true)
  })
}

export function extractTokenFromUrl(): string | undefined {
  const { token } = queryString.parse(window.location.search)
  return typeof token === 'string' && token.length > 0 ? token : undefined
}

export function calculateUpperBoundCommission(
  commissionAmount: number,
): number {
  return commissionAmount * 1.2
}

export function getCommissionRange(commissionAmount: number): string {
  const lowerBound = formatMoneyCompact(commissionAmount * 0.8, true)
  const upperBound = formatMoneyCompact(
    calculateUpperBoundCommission(commissionAmount),
    true,
  ).replace('£', '')

  return `${lowerBound}-${upperBound}`
}

export function replaceObjectKeys(
  object: Record<string, string>,
  replacements: Record<string, string>,
) {
  const newObject: Record<string, string> = {}

  for (const [key, value] of Object.entries(object)) {
    const newKey = replacements[key] ?? key
    newObject[newKey] = value
  }

  return newObject
}

// TODO: Deprecate this and replace all usages with handleApiError
export function handleSubmitFormError(
  formikContext: FormikContextType<unknown>,
) {
  const { setFieldTouched, errors } = formikContext

  // Mark all fields that have an error as touched so that we can show their
  // corresponding error message. By default, Formik only marks a field as
  // touched after submission if the field had an initial value set in the
  // initialValues prop that was not `undefined`. But sometimes values in a
  // form don't have an initial value set so this is a workaround.
  // https://github.com/jaredpalmer/formik/issues/445#issuecomment-677592750
  for (const errorPath of Object.keys(flatten(errors))) {
    setFieldTouched(errorPath, true, false)
  }

  setTimeout(scrollToFirstError, 200)
}

export function handleConstraintViolationListError({
  error,
  formikHelpers,
}: {
  error: ConstraintViolationListError

  // This is a rare case where it's ok to use `any` but in general we should
  // avoid it
  formikHelpers: FormikHelpers<any> // eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const formattedErrors = constraintViolationService.formatErrors(error)
  formikHelpers.setErrors(nestFormErrors(formattedErrors))
  scrollToFirstError()
}

/**
 * Turns violation paths like `availability[0].endTime` into the nested shape
 * Formik reads with `getIn()`, so errors reach fields inside arrays and
 * objects. Plain paths like `meetingName` are unchanged.
 *
 * A field can't show an error for itself and errors inside it at the same
 * time, so an error for the whole field (e.g. `availability`) takes precedence
 * over errors inside it (e.g. `availability[0].endTime`), whatever order the
 * API reported them in.
 */
export function nestFormErrors(formErrors: FormErrors): FormikErrors<unknown> {
  const errorEntries = Object.entries(formErrors)
  const isNestedPath = (propertyPath: string) => /[.[]/.test(propertyPath)

  let nestedErrors: FormikErrors<unknown> = {}

  for (const [propertyPath, message] of errorEntries) {
    if (isNestedPath(propertyPath)) {
      nestedErrors = setIn(nestedErrors, propertyPath, message)
    }
  }

  for (const [propertyPath, message] of errorEntries) {
    if (!isNestedPath(propertyPath)) {
      nestedErrors = { ...nestedErrors, [propertyPath]: message }
    }
  }

  return nestedErrors
}

export function extractIdentifierFromIri(iri: IRI): string {
  const urlSegments = (iri || '').split('/')

  if (urlSegments.length <= 1) {
    throw new Error(`Invalid IRI: ${iri}`)
  }

  return urlSegments[urlSegments.length - 1] || ''
}

export async function makeFileSerializable(
  file?: File | null,
): Promise<SerializableFile | null> {
  return file
    ? {
        name: file.name,
        url: URL.createObjectURL(file),
        base64: await fileToBase64(file),
      }
    : null
}

export function truncateText(
  text: string,
  options?: TruncateFileNameOptions,
): string {
  const maxLength = options?.maxLength || 40
  const truncateLength = options?.maxLength || 30

  if (text.length < maxLength) {
    return text
  }

  return text.slice(0, truncateLength) + '...'
}

interface TruncateFileNameOptions {
  maxLength?: number
  truncateLength?: number
}

export function numToWord(num: number): string {
  return capitalizeFirstLetter(numWords(num))
}

/**
 * Get the `value` but throw an error if it's null or undefined.
 */
export function getOrFail<T>(value: T, message?: string): NonNullable<T> {
  invariant(value, message)

  return value
}

export function isBottomOfDomElementVisible(element: HTMLElement): boolean {
  // Get the element's bounding rectangle
  const rect = element.getBoundingClientRect()

  // Check if the bottom of the element is above the bottom edge of the viewport
  // window.innerHeight is the height of the viewport
  return rect.bottom <= window.innerHeight
}

export function isElementOverflowingHorizontally(
  element: HTMLElement,
): boolean {
  return element.scrollWidth > element.clientWidth
}

/**
 * Whether the link is an http(s) Calendly page. Checks the host rather than
 * the whole URL so e.g. https://evil.example/?calendly.com or
 * https://calendly.com.evil.example/ aren't treated as Calendly (they'd be
 * embedded without a sandbox).
 */
export function isCalendlyUrl(url: string): boolean {
  const safeUrl = toSafeHttpUrl(url)

  if (!safeUrl) {
    return false
  }

  const { hostname } = new URL(safeUrl)

  return hostname === 'calendly.com' || hostname.endsWith('.calendly.com')
}

export function getTextFromHtmlString(html: string): string {
  const element = document.createElement('div')
  element.innerHTML = html

  return (element.textContent || '').trim()
}

export function openUrl(url: string) {
  const downloadLink = document.createElement('a')
  downloadLink.href = url
  downloadLink.click()
}

export function makeWidthAndHeight(dimension: number) {
  return {
    width: dimension,
    height: dimension,
  }
}

export function removeEmptyObjectValues<T extends object>(obj: T): T {
  const newObj: Partial<T> = {}

  for (const key of objectKeys(obj) as (keyof T)[]) {
    const value = obj[key]
    if (value) {
      newObj[key] = value
    }
  }

  return newObj as T
}

export function buildUrl(
  path: string,
  context?: {
    params?: StringMap
    searchParams?: StringMap
  },
): string {
  const params = context?.params || {}

  for (const paramName in params) {
    const paramValue = params[paramName]

    if (!paramValue) {
      throw new Error(
        `Expected paramValue to be set for paramName "${paramName}"`,
      )
    }

    path = path.replace(`:${paramName}`, paramValue.toString())
  }

  return queryString.stringifyUrl({ url: path, query: context?.searchParams })
}

/**
 * @deprecated Use buildUrl instead
 */
export function buildUrlWithQueryParams<TQueryParams = Record<string, string>>(
  url: string,
  queryParams: TQueryParams & {
    backLabel?: string
    backUrl?: string
  },
) {
  return queryString.stringifyUrl({ url, query: queryParams })
}

export function formatSelectRegulatorOption(
  regulator: RegulatorItem,
): SelectRegulatorOption {
  return {
    label: regulator.name,
    value: regulator.id.toString(),
    tooltipHint: regulator.hasDpb
      ? { text: 'This is a Designated Professional Body (DPB)' }
      : undefined,
  }
}

export function getFirstAndLastName(fullName: string) {
  const nameParts = fullName.split(/\s+/)
  const firstName = nameParts[0]

  // Treat everything after the first name as the last name
  const lastName = nameParts.slice(1).join(' ')

  return { firstName, lastName }
}

export function hasBothFirstAndLastName(fullName: string) {
  const { firstName, lastName } = getFirstAndLastName(fullName)

  return !!firstName && !!lastName
}

export function scrollToElement(element: HTMLElement | null) {
  if (!element) {
    console.error('element is not set so cannot be scrolled to')
    return
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export function buildDebugTooltip(
  data: Record<string, unknown>,
): string | undefined {
  if (!isDevelopment()) {
    return undefined
  }

  return Object.entries(data)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n')
}

export function generateUUID() {
  // Public Domain/MIT
  let d = new Date().getTime() // Timestamp
  let d2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0 // Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16 //random number between 0 and 16
    if (d > 0) {
      // Use timestamp until depleted
      r = ((d + r) % 16) | 0
      d = Math.floor(d / 16)
    } else {
      // Use microseconds since page-load if supported
      r = ((d2 + r) % 16) | 0
      d2 = Math.floor(d2 / 16)
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

export function tokenFromSnapshotUrl(url: string): string | undefined {
  const uuidPattern =
    /\/compass\/snapshots\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

  const match = url.match(uuidPattern)

  if (match) {
    return match[1]
  }

  return undefined
}

export function isNumeric(value: unknown) {
  return (
    typeof value === 'number' ||
    (typeof value === 'string' && isNumericValidator(value))
  )
}

export function extractEmailDomain(email: string): string {
  return email.split('@')[1] ?? ''
}

export function areEmailDomainsTheSame(
  email1: string,
  email2: string,
): boolean {
  const email1Domain = extractEmailDomain(email1).toLowerCase()
  const email2Domain = extractEmailDomain(email2).toLowerCase()

  return !!email1Domain && !!email1Domain && email1Domain === email2Domain
}

export function isError(value: unknown): value is Error {
  return value instanceof Error
}

export function isMacOS() {
  return navigator.platform.toLowerCase().includes('mac')
}

export function makeSortHandler<TFilters>({
  filters,
  sortByField,
  sortDirectionField,
  setFilters,
  defaultSortField,
}: {
  filters: TFilters
  sortByField: keyof TFilters
  sortDirectionField: keyof TFilters
  setFilters: (setterFunc: (prev: TFilters) => TFilters) => void
  // The field the table falls back to when unsorted. Clicking it never
  // unsorts, since that would land back on the same sort and look like the
  // click did nothing.
  defaultSortField?: string
}) {
  return function (field: string) {
    const currentDirection = String(filters[sortDirectionField] ?? '')
    const isCurrentField = field === filters[sortByField]

    // Sorting the same field cycles ASC -> DESC -> unsorted (or back to ASC
    // for the default field); picking another field (or sorting an unsorted
    // table) starts again at ASC.
    const nextDirection = getNextSortDirection({
      currentDirection,
      isCurrentField,
      isDefaultField: field === defaultSortField,
    })

    // The sort and the page reset go out as one update: consecutive URL writes
    // in the same tick build on the same starting point, so the last one wins.
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [sortByField]: nextDirection === null ? null : field,
      [sortDirectionField]: nextDirection,
    }))
  }
}

function getNextSortDirection({
  currentDirection,
  isCurrentField,
  isDefaultField,
}: {
  currentDirection: string
  isCurrentField: boolean
  isDefaultField: boolean
}): OrderDirection | null {
  if (currentDirection === ORDER_DIRECTION.asc && isCurrentField) {
    return ORDER_DIRECTION.desc
  }

  if (currentDirection === ORDER_DIRECTION.desc && isCurrentField) {
    return isDefaultField ? ORDER_DIRECTION.asc : null
  }

  return ORDER_DIRECTION.asc
}

export function moveElementToPreviousIndex<TElement>(
  element: TElement,
  array: TElement[],
): TElement[] {
  const elementIndex = array.indexOf(element)

  // Element doesn't exist in array so do nothing
  if (elementIndex === -1) {
    return array
  }

  // Element is already at the first index so nothing to do
  if (elementIndex === 0) {
    return array
  }

  const previousElement = array[elementIndex - 1]

  if (typeof previousElement === 'undefined') {
    throw new Error('Expected previousElement to be defined')
  }

  const startElements = array.slice(0, elementIndex - 1)
  const endElements = array.slice(elementIndex + 1)
  const swappedElements = [element, previousElement]

  return [...startElements, ...swappedElements, ...endElements]
}

export function moveElementToNextIndex<TElement>(
  element: TElement,
  array: TElement[],
): TElement[] {
  const elementIndex = array.indexOf(element)

  // Element doesn't exist in array so do nothing
  if (elementIndex === -1) {
    return array
  }

  // Element is already at the last index so nothing to do
  if (elementIndex === array.length - 1) {
    return array
  }

  const nextElement = array[elementIndex + 1]

  if (typeof nextElement === 'undefined') {
    throw new Error('Expected nextElement to be defined')
  }

  const startElements = array.slice(0, elementIndex)
  const endElements = array.slice(elementIndex + 2)
  const swappedElements = [nextElement, element]

  return [...startElements, ...swappedElements, ...endElements]
}

export function emptyHydraCollection(): HydraCollection<never> {
  return {
    '@context': '',
    '@id': '',
    '@type': '',
    'hydra:member': [],
    'hydra:totalItems': 0,
  }
}

/**
 * Useful for omitting empty optional form fields from a request instead of
 * sending "".
 */
export function toUndefinedIfEmpty(value: string): string | undefined {
  return value === '' ? undefined : value
}

/**
 * Useful for clearing an optional field via a PATCH request when its form
 * field is left empty (undefined would leave the field unchanged).
 */
export function toNullIfEmpty(value: string): string | null {
  return value === '' ? null : value
}

export function buildEmbedCode(embedUrl: string, height: number): string {
  return embedCodeTemplate
    .replace('{{EMBED_URL}}', embedUrl)
    .replace('{{HEIGHT}}', String(height))
}

// Reads the download file name from a Content-Disposition header, preferring
// the UTF-8 `filename*` form. Returns null when there isn't one.
export function getFileNameFromContentDisposition(
  contentDisposition: string | null | undefined,
): string | null {
  if (!contentDisposition) {
    return null
  }

  const encodedFileName = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i,
  )?.[1]

  if (encodedFileName) {
    try {
      return decodeURIComponent(encodedFileName.trim())
    } catch {
      // Malformed encoding: fall through to the plain form
    }
  }

  const plainFileName = contentDisposition.match(/filename="?([^";]+)"?/i)?.[1]

  return plainFileName ? plainFileName.trim() : null
}
